// eslint-disable-next-line import/no-unresolved
import { creditsightsSyncDataSubject$, SyncDataSubject } from '@creditsights/creditsights-web';
import { ToasterMode } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { ReactElement, useEffect, useState } from 'react';
import { UserSubscription } from '@creditsights/api-ui-web';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toasterState } from '../../atoms/toaster.atom';
import PageControl from '../../components/page-control/PageControl';
import { GENERIC_ERROR_TEXT } from '../../Constants';
import useFetchProfile from '../../hooks/user/useFetchProfile';
import useSaveProfile from '../../hooks/user/useSaveProfile';
import EmailPreferences from './email-preferences/EmailPreferences';
import Interests from './interests/Interests';
import styles from './Onboarding.module.scss';
import Role from './role/Role';
import Watchlist from './watchlist/Watchlist';
import useFetchUserPermissions from '../../hooks/user/useFetchUserPermissions';

const cx = classNames.bind(styles);

export interface OnboardingStepProps {
  show: boolean;
  onContinue: (saveFunction: () => Promise<unknown>) => void;
  onBack?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  rpOnly?: boolean;
}

interface OnboardingSaveFunctions {
  [code: string]: () => Promise<unknown>;
}

function Onboarding(): ReactElement {
  const navigate = useNavigate();
  const showToaster = useSetRecoilState(toasterState);
  const { data: user, isLoading: isProfileLoading } = useFetchProfile();
  const { mutateAsync: saveProfile } = useSaveProfile();
  const [viewIndex, setViewIndex] = useState(0);
  const [onboardingSaveFunctions, setOnboardingSaveFunctions] = useState<OnboardingSaveFunctions>({});
  const [isCompleteClicked, setIsCompleteClicked] = useState(false);

  const { data: userPermissions, isLoading: arePermissionsLoading } = useFetchUserPermissions();
  const rpOnly = userPermissions?.userSubscription === UserSubscription.RP;

  const views = [
    {
      index: 0,
      code: 'role',
      title: 'What is your role?',
      subTitle: 'Select a role that primarily describes your job. You can always modify it in future.',
    },
    {
      index: 1,
      code: 'interests',
      title: 'What are you interested in?',
      subTitle: 'Choose one or more areas of interests. You can always add more in the future.',
    },
    {
      index: 2,
      code: 'watchlist',
      title: 'Add Issuers to the Watchlist',
      subTitle: 'Watchlist lets you keep track of the Issuers you are interested in.',
    },
    {
      index: 3,
      code: 'email-preferences',
      title: 'Set up Email Preferences',
      subTitle: 'Get our content sent directly to your inbox. You can always edit email settings in your profile.',
    },
  ];

  useEffect(() => {
    if (isCompleteClicked) {
      onComplete();
    }
  }, [isCompleteClicked, onboardingSaveFunctions]);

  const onComplete = async () => {
    try {
      const promises: Promise<unknown>[] = [];
      Object.values(onboardingSaveFunctions).forEach((saveFunction) => {
        promises.push(saveFunction());
      });
      await Promise.all(promises);
      let navigateTo = '/feed';
      if (!user?.onboardingCompleted) {
        await saveProfile({ onboardingCompleted: true });
        navigateTo += '?onboarding-completed=true';
      }
      creditsightsSyncDataSubject$.next(SyncDataSubject.USER_PREFERENCE);
      navigate(navigateTo);
    } catch (e) {
      showToaster({ text: GENERIC_ERROR_TEXT, mode: ToasterMode.ERROR });
      setIsCompleteClicked(false);
    }
  };

  const onCancel = () => {
    navigate('/feed/global');
  };

  return (
    <div className={cx('body', { disabled: !user?.active || isProfileLoading || arePermissionsLoading })}>
      <PageControl count={rpOnly ? 3 : 4} active={views[viewIndex].index} />
      <div className={cx('user-account-message')}>
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        Hi {user?.firstName} {user?.lastName}. We're happy to have you onboard.
      </div>
      <div className={cx('title')}>{views[viewIndex].title}</div>
      <div className={cx('sub-title')}>{views[viewIndex].subTitle}</div>
      <div className={cx('views-ctx')}>
        <Role
          show={viewIndex === 0}
          onCancel={onCancel}
          onContinue={(saveFunction) => {
            setOnboardingSaveFunctions((prevState) => ({
              ...prevState,
              role: saveFunction,
            }));
            setViewIndex(1);
          }}
        />
        <Interests
          show={viewIndex === 1}
          onContinue={(saveFunction) => {
            setOnboardingSaveFunctions((prevState) => ({
              ...prevState,
              interests: saveFunction,
            }));
            setViewIndex(2);
          }}
          onBack={() => setViewIndex(0)}
          rpOnly={rpOnly}
        />
        <Watchlist
          show={viewIndex === 2}
          onContinue={(saveFunction) => {
            setOnboardingSaveFunctions((prevState) => ({
              ...prevState,
              watchlists: saveFunction,
            }));
            if (rpOnly) {
              setIsCompleteClicked(true);
            } else {
              setViewIndex(3);
            }
          }}
          onBack={() => setViewIndex(1)}
          rpOnly={rpOnly}
        />
        {!rpOnly && (
          <EmailPreferences
            show={viewIndex === 3}
            onContinue={(saveFunction) => {
              setOnboardingSaveFunctions((prevState) => ({
                ...prevState,
                emailPreferences: saveFunction,
              }));
              setIsCompleteClicked(true);
            }}
            onBack={() => setViewIndex(2)}
            isSaving={isCompleteClicked}
          />
        )}
      </div>
    </div>
  );
}

export default Onboarding;
